import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Divider } from "@/components/catalyst/divider";
import dayjs from "dayjs";
import { Link } from "@/components/catalyst/link";
import { pendingInvoicesQuery } from "@/app/Dashboard/GraphQL/pendingInvoicesQuery";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { InvoiceStatus } from "@/gql/graphql";
import { Badge } from "@/components/catalyst/badge";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { IllustrationsDocuments } from "@/assets/Illustrations";
import { useTranslations } from "use-intl";

export function PendingInvoicesStatBlock() {
  const t = useTranslations();
  const { supplierId } = useGlobalSupplier();
  const { data } = useSuspenseGqlQuery(pendingInvoicesQuery, {
    supplierId: supplierId ?? undefined,
  });

  const invoices = data.invoices.data;

  const getStatusBadge = (status: InvoiceStatus) => {
    if (status === InvoiceStatus.Overdue) {
      return (
        <Badge color="red">
          {t("dashboard.blocks.pendingInvoices.overdue")}
        </Badge>
      );
    }
    return (
      <Badge color="yellow">
        {t("dashboard.blocks.pendingInvoices.pending")}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader className="space-y-0">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>{t("dashboard.blocks.pendingInvoices.title")}</CardTitle>
          <Link
            to="/$tenant/$orgId/supplier/{-$supplierId}/invoices"
            params={(prev) => prev}
            className="shrink-0 text-sm"
          >
            {t("dashboard.blocks.pendingInvoices.viewMore")}
          </Link>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {invoices.length > 0 ? (
          <div className="max-h-96 overflow-y-auto px-6 pb-6">
            {invoices.map((invoice, index) => (
              <div key={invoice.id}>
                {index > 0 ? <Divider soft className="my-3" /> : null}
                <Link
                  from="/$tenant/$orgId/supplier/{-$supplierId}"
                  to="/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id"
                  params={(prev) => ({ ...prev, id: invoice.id })}
                  className="-mx-2 flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate text-sm font-medium">
                        {invoice.payee.name}
                      </div>
                      <div className="flex-shrink-0 text-xs text-gray-400">
                        #{invoice.reference}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {t("dashboard.blocks.pendingInvoices.due")}{" "}
                      {dayjs(invoice.due_date).format("MMM D, YYYY")}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-3">
                    <div className="text-sm font-semibold">
                      {formatCurrency(invoice.amount_due, invoice.currency)}
                    </div>
                    {getStatusBadge(invoice.status)}
                  </div>
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-32 opacity-80">
              <IllustrationsDocuments />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-900">
              {t("dashboard.blocks.pendingInvoices.empty")}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {t("dashboard.blocks.pendingInvoices.emptyDescription")}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
